import { Component, OnDestroy, OnInit } from '@angular/core';

import { ActivatedRoute } from '@angular/router';

import { QueryRef } from 'apollo-angular';
import { SubSink } from 'subsink';


import { GetYelpConfigGQL, GetYelpConfigQuery, GetYelpTokenGQL, GetYelpConfigQueryVariables, YelpSubscribeBusinessGQL, ZoneConfigKey } from '../../generated/graphql.generated';

import { FreyaNotificationsService } from '../services/freya-notifications.service';
import { generateUUID } from '../utilities/state.util';


@Component({
  selector: 'app-yelp',
  templateUrl: './yelp.component.html',
  styleUrls: ['./yelp.component.scss']
})
export class YelpComponent implements OnInit, OnDestroy {

  subs = new SubSink();

  loading = true;

  yelpEnabled = false;
  apiKey: string = '';
  clientId: string = '';
  refreshToken: string = '';

  authorizeEndpoint = 'https://biz.yelp.com/oauth2/authorize';
  redirectUrl = `https://${window.location.hostname}/integration/yelp`;
  stateKey = 'yelp_oauth_state';
  yelpAuthorizationLink = '';
  scopes = ['leads', 'r2r', 'r2r_get_businesses'];

  configValuesQueryRef: QueryRef<GetYelpConfigQuery, GetYelpConfigQueryVariables>;

  constructor(
    private route: ActivatedRoute,
    private getYelpConfigGQL: GetYelpConfigGQL,
    private getYelpTokenGQL: GetYelpTokenGQL,
    private yelpSubscribeBusinessGQL: YelpSubscribeBusinessGQL,
    private localNotify: FreyaNotificationsService,
  ) { }

  ngOnInit(): void {
    this.configValuesQueryRef = this.getYelpConfigGQL.watch({
      keys: [
        ZoneConfigKey.YelpIntegrationEnabled,
        ZoneConfigKey.YelpIntegrationApikey,
        ZoneConfigKey.YelpIntegrationRefreshtoken,
        ZoneConfigKey.YelpIntegrationClientid,
      ]
    });

    this.subs.sink = this.configValuesQueryRef.valueChanges.subscribe((res) => {
      this.loading = res.loading;
      if (res.loading || !res.data) return;

      const values = res.data.getZoneConfigValues;

      this.yelpEnabled = values[ZoneConfigKey.YelpIntegrationEnabled];
      this.apiKey = values[ZoneConfigKey.YelpIntegrationApikey];
      this.refreshToken = values[ZoneConfigKey.YelpIntegrationRefreshtoken];
      this.clientId = values[ZoneConfigKey.YelpIntegrationClientid];

      if (!this.refreshToken) {
        const state = generateUUID();
        localStorage.setItem(this.stateKey, state);

        this.yelpAuthorizationLink =
          `${this.authorizeEndpoint}?response_type=code` +
          `&client_id=${this.clientId}` +
          `&redirect_uri=${encodeURIComponent(this.redirectUrl)}` +
          `&scope=${this.scopes.join(' ')}` +
          `&state=${state}`;
      }
    });

    // Handle redirect back after auth
    this.subs.sink = this.route.queryParamMap.subscribe((params) => {
      const code = params.get('code');
      const state = params.get('state');

      if (!code) {
        return;
      }

      if (this.verifyState(state)) {
        this.getAccessToken(code);
      } else {
        this.localNotify.error('OAuth state mismatch');
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  subscribeToBusinesses() {
    this.yelpSubscribeBusinessGQL.mutate().subscribe(
      (res) => {
        if (res?.data?.yelpSubscribeBusiness?.success) {
          this.localNotify.success('Successfully subscribed to businesses');
        } else {
          this.localNotify.error('Subscription failed');
        }
      },
      (err) => {
        this.localNotify.apolloError('Unable to subscribe to businesses', err);
      }
    );
  }

  verifyState(state: string | null): boolean {
    const saved = localStorage.getItem(this.stateKey);
    localStorage.removeItem(this.stateKey);
    return !!state && state === saved;
  }

  getAccessToken(code: string) {
    if (!code) { return; }
    this.subs.sink = this.getYelpTokenGQL.fetch({ code }).subscribe((res) => {
      this.refreshToken = res.data.getYelpToken;
    });
  }
}
